export type OnboardingStepId =
  | "organization"
  | "knowledgebase"
  | "contacts"
  | "campaign"
  | "readiness";

export type JourneyStateKey =
  | "newAccount"
  | "onboardingIncomplete"
  | "integrationSetupNeeded"
  | "firstCampaignDraft"
  | "activeCampaign"
  | "hasResponses"
  | "needsAttention";

export type OnboardingChecklistState = {
  completedSteps: Partial<Record<OnboardingStepId, boolean>>;
  activeStep: OnboardingStepId;
  updatedAt?: string;
};

export type JourneyStateInput = {
  hasSession: boolean;
  contactListCount: number;
  contactCount: number;
  knowledgebaseCount: number;
  campaignCount: number;
  hasOrganizationProfile: boolean;
  inboxThreadCount: number;
  hasDryRun: boolean;
  blockedReasons?: string[];
  checklist?: OnboardingChecklistState;
};

export type OnboardingStep = {
  id: OnboardingStepId;
  label: string;
  complete: boolean;
  required: boolean;
};

export type ResolvedJourneyState = {
  state: JourneyStateKey;
  label: string;
  primaryActionLabel: string;
  primaryActionStep: OnboardingStepId;
  completionPercent: number;
  completedRequiredSteps: number;
  requiredStepCount: number;
  steps: OnboardingStep[];
};

export const onboardingStepOrder: OnboardingStepId[] = [
  "organization",
  "knowledgebase",
  "contacts",
  "campaign",
  "readiness",
];

export function createDefaultOnboardingState(): OnboardingChecklistState {
  return {
    activeStep: "organization",
    completedSteps: {},
  };
}

export function resolveWorkspaceJourney(input: JourneyStateInput): ResolvedJourneyState {
  const completedSteps = input.checklist?.completedSteps ?? {};
  const hasContacts = input.contactListCount > 0 && input.contactCount > 0;
  const hasOrganization = input.hasOrganizationProfile || completedSteps.organization === true;
  const hasKnowledgebase = input.knowledgebaseCount > 0;
  const hasCampaign = input.campaignCount > 0;
  const hasReadiness = input.hasDryRun || completedSteps.readiness === true;
  const hasResponses = input.inboxThreadCount > 0;

  const steps: OnboardingStep[] = [
    {
      id: "organization",
      label: "Organization",
      complete: hasOrganization,
      required: true,
    },
    {
      id: "knowledgebase",
      label: "Knowledge",
      complete: hasKnowledgebase || completedSteps.knowledgebase === true,
      required: true,
    },
    {
      id: "contacts",
      label: "Contacts",
      complete: hasContacts || completedSteps.contacts === true,
      required: true,
    },
    {
      id: "campaign",
      label: "Campaign",
      complete: hasCampaign || completedSteps.campaign === true,
      required: true,
    },
    {
      id: "readiness",
      label: "Readiness",
      complete: hasReadiness,
      required: true,
    },
  ];

  const requiredSteps = steps.filter((step) => step.required);
  const completedRequiredSteps = requiredSteps.filter((step) => step.complete).length;
  const nextRequiredStep = requiredSteps.find((step) => !step.complete);
  const primaryActionStep = nextRequiredStep?.id ?? "readiness";
  const completionPercent = Math.round((completedRequiredSteps / requiredSteps.length) * 100);

  if (!input.hasSession) {
    return {
      state: "newAccount",
      label: "Account needed",
      primaryActionLabel: "Create account",
      primaryActionStep: "organization",
      completionPercent: 0,
      completedRequiredSteps: 0,
      requiredStepCount: requiredSteps.length,
      steps,
    };
  }

  if ((input.blockedReasons ?? []).length > 0) {
    return {
      state: "needsAttention",
      label: "Needs attention",
      primaryActionLabel: "Review setup",
      primaryActionStep,
      completionPercent,
      completedRequiredSteps,
      requiredStepCount: requiredSteps.length,
      steps,
    };
  }

  if (hasResponses) {
    return {
      state: "hasResponses",
      label: "Responses waiting",
      primaryActionLabel: "Review inbox",
      primaryActionStep: "readiness",
      completionPercent,
      completedRequiredSteps,
      requiredStepCount: requiredSteps.length,
      steps,
    };
  }

  if (completedRequiredSteps === requiredSteps.length) {
    return {
      state: "activeCampaign",
      label: "Ready to manage",
      primaryActionLabel: "Review readiness",
      primaryActionStep: "readiness",
      completionPercent: 100,
      completedRequiredSteps,
      requiredStepCount: requiredSteps.length,
      steps,
    };
  }

  if (hasCampaign && !hasReadiness) {
    return {
      state: "firstCampaignDraft",
      label: "Campaign draft ready",
      primaryActionLabel: "Review readiness",
      primaryActionStep: "readiness",
      completionPercent,
      completedRequiredSteps,
      requiredStepCount: requiredSteps.length,
      steps,
    };
  }

  return {
    state: "onboardingIncomplete",
    label: "Setup in progress",
    primaryActionLabel:
      primaryActionStep === "organization"
        ? "Set up organization"
        : primaryActionStep === "knowledgebase"
          ? "Add knowledge"
          : primaryActionStep === "contacts"
            ? "Import contacts"
            : primaryActionStep === "campaign"
              ? "Create campaign"
              : "Review readiness",
    primaryActionStep,
    completionPercent,
    completedRequiredSteps,
    requiredStepCount: requiredSteps.length,
    steps,
  };
}
